import Vue from 'vue'
import _ from 'lodash'
import Helper from '@/utils/helper'
import router from '@/router'
import bugsnag from '@/bugsnag'

function getInitialState () {
    return {
        tasks: [],
        active: '',
        messages: {},
        last_sync: null,
        syncing: false
    }
}

const state = getInitialState()

const mutations = {
    'INSERT_TASK_LIST' (state, tasks) {
        // Do not do anything with null tasks
        if (tasks === null || tasks === undefined) {
            return
        }

        // Go through all tasks
        _.each(tasks, task => {
            // Fix reactive properties for messages
            if (task.messages === null || task.messages === undefined) {
                task.messages = []
            }

            // Check if this task is new or updated
            let index = _.findIndex(state.tasks, {id: task.id})

            if (index < 0) {
                // Push the new task to the array
                state.tasks.push(task)
            } else {
                // Splice the array with the updated task
                state.tasks.splice(index, 1, task)
            }
        })
    },
    'REMOVE_TASK' (state, {task, index = null}) {
        // Find the correct index
        index = _.findIndex(state.tasks, {id: task.id})

        // Remove the specified task at the index
        state.tasks.splice(index, 1)
    },
    'UPSERT_TASK' (state, {task, index = null}) {
        // Fix fauly or missing index
        if (index === null || index >= state.tasks.length) {
            index = _.findIndex(state.tasks, {id: task.id})
        }

        // Fix reaction on messages
        if (task.messages === null || task.messages === undefined) {
            task.messages = []
        }

        if (index === -1) {
            // Push the task as a new task
            state.tasks.push(task)
        } else {
            // Replace the task at the given index with the given task
            state.tasks.splice(index, 1, task)
        }
    },
    'SET_ACTIVE_TASK' (state, {task, index = null}) {
        // Fix faulty or missing index
        index = _.indexOf(state.tasks, task)

        // Set the index
        state.active = state.tasks[index].id
    },
    'REMOVE_ACTIVE_TASK' (state) {
        state.active = null
    },
    'ADD_MESSAGE' (state, {task, message, index = null}) {
        // Fix faulty or missing index
        if (index === null || index >= state.tasks.length) {
            index = _.findIndex(state.tasks, {id: task.id})
        }

        // Add the message
        if (state.tasks[index].messages === null) {
            state.tasks[index].messages = [message]
        } else {
            state.tasks[index].messages.push(message)
        }
    },
    'TASK_SYNC_DONE' (state) {
        state.last_sync = new Date().toISOString()
    },
    'TASK_SYNC_START' (state) {
        state.syncing = true
    },
    'TASK_SYNC_STOP' (state) {
        state.syncing = false
    },
    'RESET_TASK' (state) {
        state = Object.assign(state, getInitialState())
    }
}

const actions = {
    task_list: ({commit, state}, {offset = 0, everything = false}) => {
        let from = ''
        commit('TASK_SYNC_START')
        if (state.last_sync !== null && everything === false) {
            console.log('Synchronizing changes since: ' + state.last_sync)
            from = '&after=' + state.last_sync
        }

        Vue.http.get('tasks?offset=' + offset + from).then(response => {
            // Add all tasks to the storage
            commit('INSERT_TASK_LIST', response.body.data.tasks)

            // Check the amount of tasks left
            if (response.body.data.tasks !== null) {
                // Continue downloading items until we have nothing left
                actions.task_list({commit, state}, {offset: offset + response.body.data.tasks.length, everything})
            } else {
                if (offset !== 0) {
                    commit('TASK_SYNC_DONE')
                }

                commit('TASK_SYNC_STOP')
            }
        }).catch(error => {
            // It is not synchronizing any more
            commit('TASK_SYNC_STOP')

            // Log the error
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_get: ({commit}, id) => {
        Vue.http.get('task/' + id).then(response => {
            // Add the task to the storage
            commit('UPSERT_TASK', {task: response.body.data})
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_get_move: ({commit}, {id, back = false}) => {
        Vue.http.get('task/' + id).then(response => {
            // Add the task to the storage
            commit('UPSERT_TASK', {task: response.body.data})

            if (back) {
                // Go back
                router.push({ name: 'Main' })
            }
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_put: ({commit}, {task, back = false}) => {
        Vue.http.put('task/' + task.id, Helper.task.toBody(task)).then(response => {
            // Get the updated task from the server
            actions.task_get_move({commit}, {id: task.id, back})
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_delete: ({commit}, {task, index}) => {
        Vue.http.delete('task/' + task.id).then(response => {
            // Remove the task from storage
            commit('REMOVE_TASK', {task, index})
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_post: ({commit}, task) => {
        console.log('TTAASSK', task)
        Vue.http.post('tasks', Helper.task.toBody(task)).then(response => {
            actions.task_get({commit}, response.body.data)
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_select: ({commit}, {task, index}) => {
        commit('SET_ACTIVE_TASK', {task, index})
    },
    task_message: ({commit}, {task, message, index}) => {
        Vue.http.post('task/' + task.id + '/messages', { message: message }).then(response => {
            commit('ADD_MESSAGE', {task: task, message: response.body.data, index: index})
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    },
    task_status: ({commit}, {task, status}) => {
        task.status = status
        actions.task_put({commit}, {task, back: false})
    },
    task_shared_status: ({commit}, {task, status}) => {
        task.shared_status = status
        actions.task_put({commit}, {task, back: false})
    },
    task_message_read: ({commit}, {task, message, index, user}) => {
        let time = new Date().toISOString()
        Vue.http.put('task/' + task.id + '/message/' + message.id, { acknowledged_by: user.id, acknowledged_at: time }).then(response => {
            let index = _.indexOf(task.messages, message)

            if (index > -1) {
                message.acknowledged_by = user.id
                message.acknowledged_at = time
                task.messages[index] = message
                commit('UPSERT_TASK', {task})
            }
        }).catch(error => {
            bugsnag.notify(JSON.stringify(error))
        })
    }
}

const getters = {
    getFilteredTasks: (state, getters) => (content = '', orderKey = 'created_at', order = 'asc', {tag = null, category = null}, shared = 'all', favorites, deadline = 'all') => {
        // Get the current filters
        var filters = getters.getFilters

        // Run the filter chain and returned the matched tasks in the correct order
        return _.chain(state.tasks)
            .filter(task => task.status < 100)
            .filter(task => Helper.task.hasCorrectSharedStatus(task, shared, getters.getAuthenticatedUser, filters.workarea))
            .filter(task => _.includes(filters.statuses, Helper.task.getCorrectStatus(task, getters.getAuthenticatedUser, filters.workarea, getters.getWorkareas)))
            .filter(task => filters.workarea === null || filters.workarea.id === task.workarea)
            .filter(task => task.content.toLowerCase().indexOf(content.toLowerCase()) > -1)
            .filter(task => tag === null || _.includes(task.tags, tag))
            .filter(task => category === null || _.includes(task.categories, category))
            .filter(task => Helper.task.hasDeadlineFilter(task, deadline))
            .orderBy([orderKey], [order])
            .orderBy([task => _.includes(favorites, task.id) ? 1 : 0], ['desc'])
            .value()
    },
    getActiveTask: state => {
        var index = _.findIndex(state.tasks, {id: state.active})
        return state.tasks[index]
    },
    getTask: state => (id) => {
        var index = _.findIndex(state.tasks, {id: id})
        return state.tasks[index]
    }
}

export default {
    state,
    mutations,
    actions,
    getters
}



// WEBPACK FOOTER //
// ./src/vuex/modules/task.js